/*
 * liveWireAdapter.ts — the production adapter mapping real First Responder console/policy API
 * payloads into the accepted view-model shapes consumed by Connected/ and (read-only) by the
 * LivePreview view-model types.
 *
 * Originally built and reviewed as a proof (hence the file's former name,
 * `liveWireAdapterProof.ts`) that each endpoint payload COULD be mapped into the accepted shapes:
 *
 *   - compile-time: `tsc --strict` rejects any renamed, removed or retyped wire field;
 *   - runtime: the Live contract suite calls these mappings against example payloads and asserts
 *     count reconciliation, ordering and non-mutation.
 *
 * Those two properties still hold and are still enforced by the same tests — they are now
 * properties of production code, not the reason this file exists. `Connected/index.tsx` calls
 * these functions directly against live API responses.
 *
 * GRAVY-10373 Task 7: the console now reads every contract endpoint (console/policy/audits/jobs/
 * job-detail/workers/attention), so this file gains the mappers for the four that were previously
 * faked from the legacy overview endpoint via Presentation/legacyJobsAdapter.ts (now deleted):
 * toJobRow, toJobDetail, toWorkerRow, toWorkersPage, toAttentionPage, toClientFilters. The display
 * logic that adapter got right (closed-state labels/tones, countdown seconds from acts_at, the
 * selected-count/list-count invariant, timeline ordering) is carried over here, but every value is
 * now sourced from the real wire contract instead of the legacy overview shape.
 *
 * The accepted types are pulled in with `import type`, which is fully erased at compile time —
 * no runtime dependency on the preview package is created, and no fixture or derivation module is
 * touched. Referencing the accepted SHAPES is unavoidable; referencing preview DATA is not, and
 * does not happen.
 *
 * If a wire field is renamed, removed or retyped, these functions stop compiling. That is the
 * point: the traceability map proves paths EXIST, and this proves they are USABLE.
 */

import { internalWorkerLabel } from './liveWireContract';
import {
  VENUE_TIME_UNKNOWN,
  formatVenueRange,
  formatVenueTime,
} from './venueTime';
import {
  NO_ACTION_RECORDED_LABEL,
  UNKNOWN_LABEL,
} from '../LivePreview/liveMetricDisplay';
import {
  ANCHOR_SOURCE_LABELS,
  EXCLUSION_LABELS,
  SAFE_FAILURE_LABEL,
} from '../LivePreview/liveViewModel';

import type {
  FirstResponderConsoleWire,
  FirstResponderJobsWire,
  FirstResponderWorkersWire,
  FirstResponderAttentionWire,
  FirstResponderPolicyWire,
  FirstResponderPolicyAuditsWire,
  FirstResponderJobWire,
  WireJobRow,
  WireJobHistoryEntry,
  WireCandidate,
  WireSelectedCandidate,
  WirePersistedCheck,
  WireExclusionGroup,
  WireTimelineEntry,
  WireTimelineKey,
  WireDataBoundaries,
  WireErrorCode,
  WireWorkerRow,
  WireAttentionEvent,
  WireAttentionReason,
  WireGuardSkip,
  WirePolicy,
  WirePolicyAudit,
  WirePolicyClient,
  WireClientFilter,
  WireSystemAlert,
  WireSafetyStatus,
  WireJobState,
} from './liveWireContract';

import type {
  LiveHeadlineStats,
  LiveClientFilter,
  LiveResultsSummary,
  LiveDiagnostics,
  LiveJobRow,
  LiveJobHistoryEntry,
  LiveWorkerRow,
  LiveAttention,
  LiveAttentionEvent,
  LivePilotPolicy,
  LiveAuditRow,
  LiveSafetyStatus,
  LiveSystemAlert,
  LivePagination,
  LivePage,
  LiveJobDetail,
  LiveCandidate,
  LivePersistedCheck,
  LiveExclusionGroup,
  LiveTimelineEntry,
  LiveFunnel,
  LiveRunMeta,
  LiveCandidateDecision,
  LiveSignal,
  LiveTone,
  LiveNext,
  LiveGuardSkip,
  LiveRankingPolicy,
} from '../LivePreview/liveViewModel';
import {
  activityPhrase,
  MONITORED_LAST_PHRASE,
  rankingExplainerFor,
  rankingVersionLabel,
  ranksMonitoredLast,
  ranksOnActivity,
  toRankingPolicy,
} from './rankingCopy';

/*
 * Presentation the adapter owns. These are intentionally minimal — the real copy lives in the
 * preview components — but they are REAL functions, not `declare const` stubs.
 *
 * They started as ambient declarations, which compile fine and then throw at runtime because no
 * value exists. That made the whole proof compile-only and silently un-runnable. Implementing
 * them means the same mappings are exercised by the runtime tests too.
 */
// F3: the only system-alert key with a frontend-owned human label so far — every other key
// still renders its raw machine key (identity fallback below) until a real label is written for
// it. scheduler_failing is the newest closed alert key (backend F3): a newest heartbeat that
// errored, or that had failed jobs.
// Every member of WIRE_SYSTEM_ALERT_KEYS has a written label (GRAVY-10816 walk, W2): the console
// rendered the raw key `scheduler_stale` in bold above a sentence, which reads as a leak, not a name.
const ALERT_LABELS: Partial<Record<string, string>> = {
  scheduler_stale: 'Scheduler stale',
  scheduler_failing: 'Scheduler failing',
  configuration_unknown: 'Configuration unknown',
  invariant_breach: 'Invariant breach',
};
const label = (key: string): string => ALERT_LABELS[key] ?? key;
/*
 * The results window is a pilot-wide instant, not a job's, so there is no venue zone to read: it
 * renders on the shared Eastern default rather than on whatever clock the operator's browser has.
 *
 * Review comment 3 (GRAVY-10816): this label used to be built from `results.since`, which is the
 * rolling REPORTING WINDOW start, not an enable time — so a pilot enabled this morning had its
 * results headed "since enabled Aug 16", a date a month before it existed. The enable instant is
 * its own wire field now (`enabled_at`, null while the pilot is off), and it is the ONLY field
 * this label may date "since enabled" from.
 *
 * B3 (independent review): dating it from `enabled_at` UNCONDITIONALLY is the same conflation
 * running the other way. A pilot enabled sixty days ago was headed "since enabled Jul 16" over
 * counts the server only aggregated across the rolling window beneath it — and `results.since`,
 * the one field that names that window, had no consumer left at all. Both fields are read here
 * now, and the heading says which of the two it is dating from:
 *
 *   - an enable instant INSIDE the window heads the counts, because the pilot was enabled for all
 *     of the period they cover: "since enabled <enabled_at>";
 *   - an enable instant older than the window start does NOT, because the counts say nothing
 *     about the days before the window opened: the window's own start is the honest date.
 *
 * The number of days is never asserted — only the instant the wire actually sent. A hardcoded
 * "last 30 days" is a window length this payload never carried, which is the same class of claim
 * as the enable date it replaced; the length lives in graphy's REPORTING_WINDOW constant and this
 * label has no way to check it.
 *
 * A `since` the server did not substantiate takes neither branch, even with an enable instant
 * beside it: with no window start, nothing establishes that the enable happened inside the period
 * the counts cover, and that is the whole test above. The label then says the window is unknown,
 * the same answer the metric cells give for a count nobody measured. (graphy derives `since` from
 * REPORTING_WINDOW on every console response, so a null there is a broken contract, not a mode.)
 *
 * THE TWO BRANCHES ARE RENDERED DIFFERENTLY, because they name two different kinds of fact.
 *
 * The WINDOW START is a CALENDAR DATE, not an instant. graphy day-aligns it now
 * (PilotConfig.reporting_window_start — midnight UTC — shared by the builder and by the three
 * reports' default `since`), so the time of day this used to print carried no information: it was
 * the reload's own clock, ticking the heading from "since Sep 14, 8:03 PM" to "8:04 PM" over
 * counts that had not moved. A period the server aligned to a day is named by its days.
 *
 * The ENABLE INSTANT is an INSTANT and keeps its minutes. Rendered as a bare day, "since enabled
 * Sep 14" over a pilot armed at 08:00 hands it the whole of the 14th — eight hours in which it
 * could not have acted because it was not yet on, credited to it by the heading above its own
 * counts. Nothing about the day-alignment above argues for dropping these minutes: `enabled_at`
 * is a persisted, audited instant that does not move between requests, so it cannot tick the
 * heading the way the recomputed window start did. It is the same kind of fact as the policy
 * audit trail, which has always rendered to the minute.
 *
 * `formatVenueTime`'s own patterns, not a second date formatter, and the rest of the convention is
 * unchanged either way: the Eastern default for a pilot-wide instant (a `null` timezone — an
 * enable is not a venue's event and has no venue clock to read), the zone abbreviation that says
 * which clock the label is named on, and the Unknown for an instant that will not parse. The two
 * clocks deliberately differ — the day-aligned boundary is named in UTC, an operator's enable on
 * the console's shared default — because each is named on the clock it was defined against.
 */
const windowLabelFor = (
  since: string | null,
  enabledAt: string | null,
): LiveResultsSummary['windowLabel'] => {
  const enabledMs = enabledAt === null ? NaN : Date.parse(enabledAt);
  const sinceMs = since === null ? NaN : Date.parse(since);
  // An instant that will not parse names no date, exactly as in the boundary note below: it is
  // treated as absent rather than rendered, so neither branch can head the counts with "Unknown".
  if (!Number.isNaN(enabledMs) && enabledMs >= sinceMs)
    return `since enabled ${formatVenueTime(enabledAt, null, 'datetime')}`;
  // The window start is a day boundary the server aligns to midnight UTC, so it is named in UTC:
  // rendered on a US clock it would read as the evening before, a day the window does not cover.
  if (!Number.isNaN(sinceMs))
    return `since ${formatVenueTime(since, 'UTC', 'date')}`;
  return 'window unknown';
};

/*
 * F1 (GRAVY-10816 stub sweep, same class as bug 1's Why column): the Results headline was a
 * hardcoded UNKNOWN_LABEL sitting above six counts the wire had already sent. The wire carries no
 * prose, so the sentence is composed here from whichever counts the server could substantiate.
 *
 * Honesty rules, the same ones the metric cells follow (liveMetricDisplay.ts):
 *   - a null count is OMITTED from the sentence. It is not rendered as 0 (a measurement nobody
 *     took) and not rendered as the word Unknown (which would read as a count of Unknown things);
 *   - a PROVEN zero is reported as 0, because "we measured none" is a real result;
 *   - a window with nothing recorded at all says exactly that, once.
 *
 * The order is the funnel's, not the wire object's: what was acted on, what went out, what came
 * back, what it produced, then what went wrong. The preview composes the same kind of sentence
 * from its own facts (derive.ts) — the vocabulary is shared, the numbers never are.
 */
const CLAUSES: {
  key: keyof FirstResponderConsoleWire['results'];
  phrase: (n: number) => string;
}[] = [
  {
    key: 'shifts_acted_on',
    phrase: (n) => `${n} shift${n === 1 ? '' : 's'} acted on`,
  },
  {
    key: 'offers_sent',
    phrase: (n) => `${n} offer${n === 1 ? '' : 's'} sent`,
  },
  { key: 'accepted', phrase: (n) => `${n} accepted` },
  {
    key: 'filled_by_first_responder',
    phrase: (n) => `${n} shift${n === 1 ? '' : 's'} filled by First Responder`,
  },
  {
    key: 'no_shows_or_drops',
    phrase: (n) =>
      `${n} no-show${n === 1 ? '' : 's'} or drop${n === 1 ? '' : 's'}`,
  },
  {
    key: 'failed_actions',
    phrase: (n) => `${n} failed action${n === 1 ? '' : 's'}`,
  },
];

export const headlineFor = (
  r: FirstResponderConsoleWire['results'],
): string => {
  const parts = CLAUSES.flatMap(({ key, phrase }) => {
    const value = r[key];
    return typeof value === 'number' ? [phrase(value)] : [];
  });
  return parts.length === 0
    ? 'No results recorded yet for this window.'
    : `${parts.join(', ')}.`;
};

// Closed state -> label/tone. The wire deliberately carries no display strings (see
// liveWireContract.ts's own doc comment) — this is the ONE place both toJobRow and toJobDetail
// compose them, mirroring LivePreview/derive.ts's STATE_META so a real row and a fixture row in
// the same state render identically. Duplicated rather than imported: liveWireAdapter.ts must
// never reach derive.ts (a fixture/business-logic module — see __tests__/fixtureBoundary.test.ts).
const STATE_META: Record<WireJobState, { label: string; tone: LiveTone }> = {
  watching: { label: 'WATCHING', tone: 'blue' },
  waiting: { label: 'WAITING', tone: 'blue' },
  shadow_selected: { label: 'WOULD OFFER', tone: 'blue' },
  offers_sent: { label: 'OFFERS SENT', tone: 'amber' },
  filled: { label: 'FILLED', tone: 'green' },
  needs_attention: { label: 'NEEDS ATTENTION', tone: 'red' },
  skipped: { label: 'SKIPPED', tone: 'plain' },
  failed: { label: 'FAILED', tone: 'red' },
};

/*
 * S1 (independent review), the other two sites of the same defect it fixed on the decision badge.
 * Both state lookups — the jobs LIST (toJobRow) and the job MODAL (toJobDetail) — used to index
 * this map and read `.label` straight off the result, which leans on a compile-time property of
 * THIS repository as if it were a runtime guarantee about the server's vocabulary. A state graphy
 * persists before this contract catches up is `undefined` here, and `.label` on undefined throws:
 * one unknown row takes down the whole jobs list, and one unknown detail takes down the modal the
 * operator just opened.
 *
 * Same answer as decisionLabelFor and timelineLabelFor: humanise the key — underscores to spaces,
 * in the upper case the badge itself is written in — and leave the tone neutral, because a tone
 * is a claim about the outcome and an unrecognised state recorded none.
 */
const stateLabelFor = (s: WireJobState): string =>
  STATE_META[s]?.label ?? s.replace(/_/g, ' ').toUpperCase();
const stateToneFor = (s: WireJobState): LiveTone =>
  STATE_META[s]?.tone ?? 'plain';

/*
 * GRAVY-10816 follow-up (Correction 3): a history entry's `run_state` is the RAW persisted value,
 * never passed through the backend's job-level FILLED upgrade. To reuse STATE_META's words for it
 * rather than inventing a second label vocabulary, it is first translated through this mirror of
 * `FirstResponder::JobsBuilder::RUN_STATE_TO_WIRE` (minus that upgrade, which has no meaning for a
 * run that no longer speaks for the job) — same duplicate-rather-than-import ruling as STATE_META
 * itself: this file must never reach a backend source file.
 */
const HISTORY_RUN_STATE_TO_WIRE: Partial<Record<string, WireJobState>> = {
  evaluating: 'watching',
  deferred: 'waiting',
  shadow_selected: 'shadow_selected',
  completed: 'offers_sent',
  needs_attention: 'needs_attention',
  skipped: 'skipped',
  failed: 'failed',
};
const historyStateLabelFor = (runState: string): string => {
  const wire = HISTORY_RUN_STATE_TO_WIRE[runState];
  return wire
    ? stateLabelFor(wire)
    : runState.replace(/_/g, ' ').toUpperCase();
};
const historyStateToneFor = (runState: string): LiveTone => {
  const wire = HISTORY_RUN_STATE_TO_WIRE[runState];
  return wire ? stateToneFor(wire) : 'plain';
};

// Closed anchor source -> label (ANCHOR_SOURCE_LABELS, liveViewModel.ts). A source the contract
// has not caught up with is humanised, never shown as its raw key — the same fallback every other
// closed-key label in this file uses.
const anchorSourceLabelFor = (source: string | null): string =>
  source === null
    ? UNKNOWN_LABEL
    : ANCHOR_SOURCE_LABELS[source] ?? capitalize(source.replace(/_/g, ' '));

// One earlier activation of a job that reopened. Already present on the jobs-list payload, so
// mapping it costs zero additional network calls (Correction 5) — opening one specific historical
// run's full detail still fetches, exactly as opening the main row already does.
export const toHistoryEntry = (
  h: WireJobHistoryEntry,
  timezone: string,
): LiveJobHistoryEntry => ({
  runId: h.run_id,
  anchorAt: formatVenueTime(h.anchor_at, timezone, 'datetime'),
  anchorSourceLabel: anchorSourceLabelFor(h.anchor_source),
  stateLabel: historyStateLabelFor(h.run_state),
  tone: historyStateToneFor(h.run_state),
  stateReason: h.state_reason,
});

// Countdown only for a real, still-watching row with an acts_at to count down to; every other
// state (and a watching row with no acts_at yet) is a resolved/pending text line, never a
// countdown. Mirrors legacyJobsAdapter.ts's toLiveNext exactly, sourced from the wire's own acts_at
// instead of the legacy overview's trigger_at.
//
// M6 correction: acts_at is the TRIGGER time, never a past "evaluated" timestamp — the text
// fallback used to label every state "Evaluated <acts_at>", which misreads a future or in-the-
// moment trigger as an already-completed evaluation. A resolved (non-watching) row was TRIGGERED
// at this instant.
//
// Task 11: the text fallback renders acts_at on the VENUE's clock ("Triggered Sep 6, 9:46 AM
// CDT"), never as a raw UTC ISO string and never on the operator's browser clock. An acts_at that
// names no instant — null, or a value that will not parse — renders the same honest Unknown
// either way, instead of echoing the unusable value back at the operator. That retires the old
// "Acts at <raw value>" line: a watching row with a readable acts_at always counts down to it, so
// the only watching row left for that line to describe was one whose acts_at was unreadable.
const nextForJobRow = (
  state: WireJobState,
  actsAt: string | null,
  nowMs: number,
  timezone: string,
  nextEvalAt: string | null = null,
): LiveNext => {
  // A waiting activation counts down to its RETURN time, not its old trigger.
  if (state === 'waiting') {
    const wakeMs = nextEvalAt === null ? NaN : Date.parse(nextEvalAt);
    if (Number.isNaN(wakeMs))
      return { kind: 'text', text: 'Waiting to look again' };
    return {
      kind: 'countdown',
      secondsRemaining: Math.round((wakeMs - nowMs) / 1000),
      purpose: 'look_again',
    };
  }
  const actsMs = actsAt === null ? NaN : Date.parse(actsAt);
  if (Number.isNaN(actsMs)) return { kind: 'text', text: UNKNOWN_LABEL };
  if (state === 'watching') {
    return {
      kind: 'countdown',
      secondsRemaining: Math.round((actsMs - nowMs) / 1000),
    };
  }
  return {
    kind: 'text',
    text: `Triggered ${formatVenueTime(actsAt, timezone, 'datetime')}`,
  };
};

/*
 * Nulls are preserved end to end. A `?? 0` here would be the exact bug this proof exists to stop.
 *
 * ONE NUMBER, ONE PERIOD (delta review). Two of these four tiles were captioned 'last 24 h' — a
 * period no payload has ever carried. `stats.offers_sent` and `stats.shifts_filled` are read by
 * ConsoleBuilder out of the SAME `result.outcomes` hash that feeds the Results panel's
 * `offers_sent` and `filled_by_first_responder` ("the SAME FACT the headline tile renders", in the
 * builder's own words), and that report covers PilotConfig::REPORTING_WINDOW — thirty days. So the
 * tile was not merely unsourced, it was wrong by a factor of thirty, and it sat directly above a
 * panel captioning the identical number "since Aug 16 UTC". An operator quoting either number was
 * contradicted by the other one further down the same screen.
 *
 * A windowed tile therefore carries `windowLabelFor`'s answer for THIS payload — not a copy of its
 * logic, the value itself — so the caption can only ever move with the panel's.
 *
 * The other two tiles are NOT windowed and keep their own honest captions. `watching_now` is the
 * size of a live cohort (FirstResponder::WatchQuery — the population the pilot is watching at this
 * instant, which is what "now" says) and `needs_attention` is AttentionQuery's unresolved
 * population, a backlog that is true until someone clears it rather than a count over a period.
 * Borrowing the reporting window for either would be the same defect in the opposite direction.
 */
export const toStats = (w: FirstResponderConsoleWire): LiveHeadlineStats => {
  const windowLabel = windowLabelFor(w.results.since, w.results.enabled_at);
  return {
    watchingNow: { value: w.stats.watching_now, windowLabel: 'now' },
    offersSent: { value: w.stats.offers_sent, windowLabel },
    shiftsFilled: { value: w.stats.shifts_filled, windowLabel },
    needsAttention: {
      value: w.stats.needs_attention,
      windowLabel: 'unresolved',
    },
  };
};

// GRAVY-10373 Task 7: filters come 1:1 from the console payload's own filters array — no
// synthesized "All clients" chip, no "Unknown" chip unless the server itself emits a row with
// customer_id: null. `clients` is the ONE pilot cohort list (policyWire.active.clients) — never a
// second customer list — used only to resolve a customer_id to its display name.
export const toClientFilters = (
  filters: WireClientFilter[],
  clients: WirePolicyClient[],
): LiveClientFilter[] =>
  filters.map((f) => ({
    key: f.key,
    label:
      f.customer_id === null
        ? f.key === 'all'
          ? 'All clients'
          : 'Unknown'
        : (clients.find((c) => c.customer_id === f.customer_id)?.name ??
          `Customer #${f.customer_id}`),
    watching: f.watching,
    filled: f.filled,
  }));

// The BASIS is carried through, not dropped. With three closed bases the UI must be able to tell
// "nothing can happen" (effective_off) from "something ran and nothing happened"
// (shadow_zero_action_proof) from "LIVE ran and every action is bounded by persisted proof"
// (live_bounded_action_proof) — and a test can then assert that a `clear` is consistent with the
// mode beside it, which was previously unrepresentable downstream of this adapter. toSafety is
// basis-agnostic by construction: a new closed basis needs no code change here, only a new
// closed-set member on the wire (liveWireContract.ts) and view-model (liveViewModel.ts) sides.
export const toSafety = (s: WireSafetyStatus): LiveSafetyStatus =>
  s.state === 'clear'
    ? { state: 'clear', basis: s.basis }
    : s.state === 'unknown'
      ? { state: 'unknown', reason: s.reason }
      : { state: 'breached', reasons: s.reasons };

export const toResults = (
  w: FirstResponderConsoleWire,
): LiveResultsSummary => ({
  windowLabel: windowLabelFor(w.results.since, w.results.enabled_at),
  shiftsActedOn: w.results.shifts_acted_on,
  filledByFirstResponder: w.results.filled_by_first_responder,
  offersSent: w.results.offers_sent,
  accepted: w.results.accepted,
  noShowsOrDrops: w.results.no_shows_or_drops,
  failedActions: w.results.failed_actions,
  headline: headlineFor(w.results),
  metabaseUrl: w.results.metabase_url,
});

export const toSystemAlert = (a: WireSystemAlert): LiveSystemAlert => ({
  key: a.key,
  label: label(a.key),
  detail: a.detail,
});

export const toGuardSkip = (s: WireGuardSkip): LiveGuardSkip => ({
  reason: s.reason,
  count: s.count,
});

/*
 * F5 (GRAVY-10816 stub sweep): the diagnostics note was hardcoded `null`, which meant
 * `data_available_from` — the payload's four "earliest instant this family has data for"
 * boundaries — was read NOWHERE in the console. A thin diagnostics card was therefore ambiguous
 * in the worst possible way: an operator could not tell "the pilot barely acted" from "we only
 * started recording this yesterday". The boundary is the one fact that separates them.
 *
 * A null boundary is the server saying it cannot substantiate that family AT ALL, so it is left
 * out of the sentence rather than given a date; all four null leaves the note null and the
 * section renders no note, exactly as before.
 *
 * Rendered as a plain UTC calendar date, not through venueTime.ts: a recording boundary is a
 * pilot-wide bookkeeping fact with no venue and no clock time worth showing, so it is reported on
 * the one shared clock the wire itself uses rather than on a venue's or the browser's.
 *
 * M3 (independent review): the four families are recorded from the SAME instant in every payload
 * graphy sends, so the note printed one date four times — four clauses that read as four separate
 * findings when there is one. The families are grouped by the date they share, in payload order
 * inside each group and first-seen order between them, so the sentence is as long as the number
 * of distinct boundaries and no longer.
 */
const BOUNDARY_LABELS: [keyof WireDataBoundaries, string][] = [
  ['stats', 'stats'],
  ['results', 'results'],
  ['diagnostics', 'diagnostics'],
  ['attention', 'attention'],
];

const utcDateFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

export const dataBoundaryNote = (b: WireDataBoundaries): string | null => {
  // A Map keeps the insertion order of the dates, so the groups come out in the order the payload
  // first names them and the families inside a group stay in BOUNDARY_LABELS order.
  const families = new Map<string, string[]>();
  BOUNDARY_LABELS.forEach(([key, label]) => {
    const iso = b[key];
    // An instant that will not parse names no date; saying nothing about that family beats
    // printing "Invalid Date" at an operator.
    if (iso === null || Number.isNaN(Date.parse(iso))) return;
    // Grouped on the RENDERED date, not the instant: two boundaries hours apart on the same day
    // are one date on screen, and printing it twice is the repetition this grouping removes.
    const date = utcDateFormat.format(new Date(iso));
    families.set(date, [...(families.get(date) ?? []), label]);
  });
  const parts = Array.from(
    families,
    ([date, group]) => `${date} (${group.join(', ')})`,
  );
  return parts.length === 0 ? null : `Data recorded since ${parts.join(', ')}`;
};

export const toDiagnostics = (
  w: FirstResponderConsoleWire,
): LiveDiagnostics => ({
  offersCreated: w.diagnostics.offers_created,
  pushSent: w.diagnostics.push_sent,
  smsSent: w.diagnostics.sms_sent,
  observedModes: w.diagnostics.observed_modes,
  systemAlerts: w.diagnostics.system_alerts.map(toSystemAlert),
  // null is carried through, never flattened to []: "the mode had no attempts, so nothing is
  // known" and "attempts ran and no guard stopped one" are different claims.
  guardSkips: w.diagnostics.guard_skips?.map(toGuardSkip) ?? null,
  note: dataBoundaryNote(w.data_available_from),
});

// id is the BUSINESS job id (job_id), never the run id (WireJobRow.id) — the detail/workers routes
// are keyed by job id, and this value is threaded through onSelectJob to fetch them. `nowMs` is
// computed once per fetched jobs page by the caller (Connected/index.tsx), so a row's countdown
// secondsRemaining stays stable while LiveConsole's own useCountdown ticker animates it — the same
// pattern legacyJobsAdapter.ts used.
export const toJobRow = (r: WireJobRow, nowMs: number): LiveJobRow => ({
  id: r.job_id,
  // The run this row describes (Gap B). A still-watching row's wire id is the synthetic
  // "watch-<job_id>" handle, which names no run.
  runId: r.id.startsWith('watch-') ? null : r.id,
  title: r.title,
  client: r.client,
  state: r.state,
  stateLabel: stateLabelFor(r.state),
  tone: stateToneFor(r.state),
  next: nextForJobRow(
    r.state,
    r.acts_at,
    nowMs,
    r.timezone,
    r.next_eval_at ?? null,
  ),
  workersAvailable: r.workers_available,
  // A real 'watching' row has no persisted run yet (id "watch-<job_id>"; GET .../jobs/:id 404s
  // for it) — never clickable. Every other state is a recorded run and safe to open.
  clickable: r.state !== 'watching',
  history: r.history.map((h) => toHistoryEntry(h, r.timezone)),
});

const capitalize = (s: string): string =>
  s.charAt(0).toUpperCase() + s.slice(1);

// GRAVY-10816 review-app QA, bug 1 (2026-09-14): the Why column read "Unknown" on every row while
// tier, score, distance, favorite and shifts_with_client sat in the same row. The wire carries
// FACTS only (liveWireContract.ts, NO EXPLANATION VOCABULARY); this is the frontend-owned sentence
// composed from those facts, read in the order ranking v2 reads them: tier, then distance, then
// relationship, then score. A fact the server did not record says so ("distance unknown"), never a
// blank, and a worker with no usable tier is named as not selectable rather than explained away.
// Nothing here is a claim the policy did not make: no score-versus-score comparison, no ranking
// position other than the persisted selection_rank.
// RANKED BY THE RUN'S OWN VERSION (rankingCopy.ts). A version three run leads with what ranked the
// worker before tier — monitored last, then activity points — and a version one or two run reads
// exactly as it always has. `ranking` defaults to null (version one) only for callers that have no
// run context; every live caller passes the run's recorded version.
export const explainWorkerRow = (
  r: Pick<
    WireWorkerRow,
    | 'eligibility'
    | 'tier'
    | 'score'
    | 'distance_miles'
    | 'favorite'
    | 'shifts_with_client'
    | 'selection_rank'
  > &
    Partial<
      Pick<WireWorkerRow, 'activity_points' | 'worked_60d' | 'said_yes_60d'>
    >,
  ranking: LiveRankingPolicy | null = null,
): string => {
  if (r.tier === null || r.eligibility === 'unselectable_unknown') {
    return 'Not selectable: no usable Dependability history (shown for transparency, never auto-selected)';
  }
  const parts: string[] = [];
  if (ranksOnActivity(ranking)) {
    parts.push(
      activityPhrase({
        activity_points: r.activity_points ?? null,
        worked_60d: r.worked_60d ?? null,
        said_yes_60d: r.said_yes_60d ?? null,
      }),
    );
  }
  // Under a monitored-last version the tier says where it put the worker — after the rest of the
  // band, not after everyone (bands are ordered separately).
  parts.push(
    ranksMonitoredLast(ranking) && r.tier === 'monitored'
      ? `${capitalize(r.tier)} tier, ${MONITORED_LAST_PHRASE}`
      : `${capitalize(r.tier)} tier`,
  );
  parts.push(
    r.distance_miles === null
      ? 'distance unknown'
      : `${r.distance_miles} mi away`,
  );
  if (r.favorite) parts.push('favorite of this client');
  if (r.shifts_with_client === null) {
    parts.push('client history unknown');
  } else if (r.shifts_with_client > 0) {
    parts.push(
      `worked ${r.shifts_with_client} shift${r.shifts_with_client === 1 ? '' : 's'} for this client`,
    );
  } else {
    parts.push(r.favorite ? 'no shifts here yet' : 'new to this client');
  }
  parts.push(r.score === null ? 'score unknown' : `score ${r.score}`);
  const basis = parts.join(' · ');
  if (r.selection_rank !== null)
    return `Selected #${r.selection_rank}: ${basis}`;
  if (r.eligibility === 'selected') return `Selected: ${basis}`;
  return `Not in the wave: ${basis}`;
};

// The ONLY identity transformation: displayName is derived from worker_id, never from a name.
export const toWorkerRow = (
  r: WireWorkerRow,
  ranking: LiveRankingPolicy | null = null,
): LiveWorkerRow => ({
  id: String(r.worker_id),
  displayName: internalWorkerLabel(r.worker_id),
  eligibility: r.eligibility,
  tier: r.tier,
  score: r.score,
  distanceMiles: r.distance_miles,
  favorite: r.favorite,
  shiftsWithClient: r.shifts_with_client,
  selectionRank: r.selection_rank,
  // Composed from the row's own persisted facts (explainWorkerRow above); never a blank cell.
  why: explainWorkerRow(r, ranking),
});

// Closed reason_class -> operator-facing label. The wire carries the machine key only (see
// liveWireContract.ts's NO EXPLANATION VOCABULARY note) — this is the frontend-owned sentence.
const ATTENTION_REASON_LABELS: Record<WireAttentionReason, string> = {
  push_failed: 'Push delivery failed',
  push_no_device: 'No device to receive a push',
  sms_no_phone: 'No phone number to receive an SMS',
  sms_failed: 'No push device, and no text confirmed sent',
  transport_error: 'Invitation could not be created',
  invalid_offer: 'Invitation was refused',
};

/*
 * S1 again, and this is the site where the missing guard was already VISIBLE rather than merely
 * fatal. The two reason lookups below (the attention row and the card's summary line) indexed this
 * map directly; the summary interpolates the result into a template string, so a reason_class this
 * contract has not caught up with printed the literal word "undefined" to an operator —
 * "Worker #501 · undefined" — as the console's explanation of why someone could not be reached.
 *
 * Humanised the same way as the badges, but in SENTENCE case: these labels are read as prose next
 * to the worker ("Push delivery failed"), not as a badge. An unknown reason still names the class
 * the server recorded, which is the one thing that stays actionable.
 */
const attentionReasonFor = (r: WireAttentionReason): string =>
  ATTENTION_REASON_LABELS[r] ?? capitalize(r.replace(/_/g, ' '));

// F11: the event names the WORKER it is about. Every attention class describes an invitation that
// could not reach one specific person — "no device on file", "no phone number" — so a row without
// the worker states a problem the operator cannot act on, and two failures on one job were
// indistinguishable. Identity stays internal: the id maps through internalWorkerLabel, the same
// single transformation every other worker label in this console uses. No name is on the wire.
export const toAttentionEvent = (
  e: WireAttentionEvent,
): LiveAttentionEvent => ({
  workerId: e.worker_id,
  workerLabel: internalWorkerLabel(e.worker_id),
  reasonClass: e.reason_class,
  reason: attentionReasonFor(e.reason_class),
  raisedAt: e.raised_at,
  resolution: e.resolution,
  resolvedAt: e.resolved_at,
});

// The card's one-line summary. LiveAttentionItem carries a single sentence rather than separate
// fields, so the worker leads it and the reason follows — "Worker #501 · Push delivery failed" —
// which is also the order the operator reads: who could not be reached, then why.
const attentionSummaryFor = (e: WireAttentionEvent): string =>
  `${internalWorkerLabel(e.worker_id)} · ${attentionReasonFor(e.reason_class)}`;

// GRAVY-10373 Task 7 / I2 correction: the Needs Attention CARD, built from the real attention
// endpoint instead of being derived from job rows. The card renders `items` (the open-filtered
// page) as its list, but `count` is the wire's own unresolved_count — the TRUE total across every
// page, not merely how many open rows happen to be on this one page. Counting only this page's
// rows silently under-reported whenever the true total exceeded one page (25 rows) — see
// NeedsAttentionCard's "Showing N of M" note for how the two numbers stay reconcilable in the UI
// when they differ. Falls back to this page's own open-row count only when the wire cannot
// substantiate a total (unresolved_count null) — never a fabricated number, and never fewer than
// what is visibly on the page. Resolution is filtered to 'open' for the list — acknowledged/
// archived rows are history, not something that still needs attention right now.
export const toAttentionPage = (
  w: FirstResponderAttentionWire,
): LiveAttention => {
  const open = w.items.filter((e) => e.resolution === 'open');
  if (open.length === 0) return null;
  return {
    count: w.unresolved_count ?? open.length,
    items: open.map((e) => ({
      jobId: e.job_id,
      reasonClass: e.reason_class,
      summary: attentionSummaryFor(e),
    })),
  };
};

export const toPolicy = (p: WirePolicy): LivePilotPolicy => ({
  clients: p.clients.map((c) => ({ id: c.customer_id, name: c.name })),
  waitBeforeEvaluationMinutes: p.wait_before_evaluation_minutes,
  minLeadMinutes: p.min_lead_minutes,
  maxOffersPerJob: p.max_offers_per_job,
  distanceLimitMiles: p.distance_limit_miles,
  treatmentPercent: p.treatment_percent,
  version: p.version,
  ranking: toRankingPolicy(p.ranking),
});

// The audit's `setting` is the persisted column name (`ranking_policy_version_id`), which the table
// rendered verbatim (GRAVY-10816 walk, W4). These are the names the settings panel already uses for
// the same facts; a key outside the list is humanised, never printed as a machine key.
const AUDIT_SETTING_LABELS: Partial<Record<string, string>> = {
  status: 'Status',
  ranking_policy_version_id: 'Ranking policy version',
  allocation_version_id: 'Allocation version',
  wait_before_evaluation_minutes: 'Wait before evaluation',
  min_lead_minutes: 'Minimum lead time',
  max_offers_per_job: 'Max offers per job',
  distance_limit_miles: 'Distance limit',
  treatment_percent: 'Live send share',
  clients: 'Clients',
  customer_ids: 'Clients',
  version_one_seed: 'Version one seed',
};
export const auditSettingLabel = (setting: string): string => {
  const known = AUDIT_SETTING_LABELS[setting];
  if (known) return known;
  const words = setting.replace(/_id$/, '').replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// GRAVY-10816 second sweep: a system actor reached the WHO column as its task name
// ("first_responder:publish_ranking_v2"). WHO is read as a person or as the system, so the task
// name becomes "System (publish ranking v2)". A person's name passes through untouched.
export const auditActorLabel = (who: string): string => {
  const m = /^first_responder:(.+)$/.exec(who);
  return m ? `System (${m[1].replace(/_/g, ' ')})` : who;
};

// A policy change is a pilot-wide event, not a job's, so — like the results window — it renders on
// the shared Eastern default rather than the operator's browser clock.
export const toAuditRow = (a: WirePolicyAudit): LiveAuditRow => ({
  at: formatVenueTime(a.occurred_at, null, 'datetime'),
  who: auditActorLabel(a.who),
  setting: auditSettingLabel(a.setting),
  from: a.from_value,
  to: a.to_value,
  reason: a.reason,
});

export const toPagination = (
  p: FirstResponderJobsWire['pagination'],
): LivePagination => ({
  total: p.total,
  page: p.page,
  perPage: p.per_page,
  totalPages: p.total_pages,
});

export const toWorkersPage = (
  w: FirstResponderWorkersWire,
): LivePage<LiveWorkerRow> & { candidatesTotal: number | null } => ({
  // Each row is explained by the version THIS run ranked under, carried on the page itself.
  items: w.items.map((r) => toWorkerRow(r, toRankingPolicy(w.ranking))),
  pagination: toPagination(w.pagination),
  candidatesTotal: w.candidates_total,
});

// `draft` wraps as a LiveSettingsDraft (policy + status + basedOnVersion) — SettingsProps.draft
// needs the wrapper, not a bare LivePilotPolicy. `based_on_version` is typed `string | null` on
// the wire; an existing draft row always carries the value it was created with, so `?? ''` is a
// defensive fallback for an unexpected null, not the expected path.
export const mapPolicy = (w: FirstResponderPolicyWire) => ({
  active: w.active ? toPolicy(w.active) : null,
  draft: w.draft
    ? {
        policy: toPolicy(w.draft.policy),
        status: 'draft' as const,
        basedOnVersion: w.draft.based_on_version ?? '',
      }
    : null,
  constraints: w.constraints,
});

// ---- request-body builders: view-model/form -> wire, the reverse direction ----
// These are new (the adapter previously only mapped wire responses -> view model). Pure
// camelCase -> snake_case mappers; Connected/index.tsx is the sole caller.

export const toSaveDraftRequestBody = (
  policy: LivePilotPolicy,
  basedOnVersion: string,
  expectedDraftVersion: string | null,
  reason: string,
) => ({
  policy: {
    wait_before_evaluation_minutes: policy.waitBeforeEvaluationMinutes,
    min_lead_minutes: policy.minLeadMinutes,
    max_offers_per_job: policy.maxOffersPerJob,
    distance_limit_miles: policy.distanceLimitMiles,
    treatment_percent: policy.treatmentPercent,
    customer_ids: policy.clients.map((c) => c.id),
  },
  based_on_version: basedOnVersion,
  expected_draft_version: expectedDraftVersion,
  reason,
});

// C1 correction: "no policy body" (activate accepts no policy object) does NOT mean "no reason".
// The backend consumes params[:reason] in PolicyRequestDigest/PolicyActivation, persists it as
// activation_reason, records it in both audit rows, and the active-config model refuses a blank
// activation_reason — this parameter must never be silently dropped by a caller.
export const toActivateRequestBody = (
  basedOnVersion: string,
  expectedDraftVersion: string | null,
  reason: string,
) => ({
  based_on_version: basedOnVersion,
  expected_draft_version: expectedDraftVersion,
  reason,
});

// Seventh endpoint: audits paginate on their own route, never embedded in the policy payload.
export const mapPolicyAudits = (w: FirstResponderPolicyAuditsWire) => ({
  items: w.items.map(toAuditRow),
  pagination: toPagination(w.pagination),
});

// ---- job detail: the complete recorded run ----

/*
 * F3 (GRAVY-10816 stub sweep): the decision badge rendered the raw wire key ("will_offer") and
 * painted every decision the same neutral grey, so an ACCEPTED offer and a WITHDRAWN one were
 * visually identical in the modal's candidate list.
 *
 * Closed set -> label/tone, the same duplicate-rather-than-import ruling as STATE_META above: the
 * words and colours mirror LivePreview/derive.ts so a recorded run and a fixture run read alike,
 * but this file must never reach derive.ts (__tests__/fixtureBoundary.test.ts). Exhaustive by
 * type: a new closed decision cannot be ADDED to the wire vocabulary without being given words
 * here, because the Record stops compiling until it is.
 *
 * S1 (independent review): that is a compile-time property of THIS repository, and the lookups
 * below used to lean on it as if it were a runtime guarantee — they indexed the map and read
 * `.label` straight off the result, so a decision key graphy persisted before this contract
 * caught up threw a TypeError and took the whole job modal down with it. The backend owns the
 * vocabulary; the honest handling of a key it has moved ahead on is the same one the timeline
 * uses (timelineLabelFor): humanise it, in the upper case the badge is written in, and leave the
 * tone neutral rather than borrowing one that would claim an outcome nobody recorded.
 *
 * The tones carry the same meaning as everywhere else in the console: green is a decision that
 * went as intended, red is one an operator may need to act on, plain is bookkeeping.
 */
const DECISION_META: Record<
  LiveCandidateDecision,
  { label: string; tone: LiveTone }
> = {
  offer: { label: 'OFFER', tone: 'green' },
  will_offer: { label: 'WILL OFFER', tone: 'green' },
  accepted: { label: 'ACCEPTED', tone: 'green' },
  // The shift filled elsewhere and this offer was closed behind it. Nothing went wrong and
  // nothing needs doing: bookkeeping, hence plain.
  closed: { label: 'CLOSED', tone: 'plain' },
  withdrawn: { label: 'WITHDRAWN', tone: 'red' },
};

export const decisionLabelFor = (d: LiveCandidateDecision): string =>
  DECISION_META[d]?.label ?? d.replace(/_/g, ' ').toUpperCase();
export const decisionToneFor = (d: LiveCandidateDecision): LiveTone =>
  DECISION_META[d]?.tone ?? 'plain';

/*
 * F12 (GRAVY-10816 stub sweep): a hardcoded empty signals array left the expandable evidence row
 * under every selected candidate permanently empty, while the row's own tier, score, favorite and
 * shifts_with_client sat on the wire. Same class as bug 1's Why column, composed the same way.
 *
 * The chips carry what the candidate table's COLUMNS do not already show (rank, name, tier,
 * score, distance, decision): the relationship facts, plus the dependability pair read as one
 * value. A fact the server did not record produces NO chip — a measured absence. An "Unknown"
 * chip would occupy the same space as evidence while being the absence of it, which is exactly
 * the confusion the null-versus-zero rule exists to prevent.
 */
export const candidateSignals = (
  c: Pick<WireCandidate, 'tier' | 'score' | 'favorite' | 'shifts_with_client'> &
    Partial<
      Pick<WireCandidate, 'activity_points' | 'worked_60d' | 'said_yes_60d'>
    >,
  ranking: LiveRankingPolicy | null = null,
): LiveSignal[] => {
  const signals: LiveSignal[] = [];
  // Only where the run RANKED on activity: a v2 run records it too, but it did not order anyone.
  if (ranksOnActivity(ranking)) {
    signals.push({
      label: 'Recent activity',
      value: activityPhrase({
        activity_points: c.activity_points ?? null,
        worked_60d: c.worked_60d ?? null,
        said_yes_60d: c.said_yes_60d ?? null,
      }),
    });
  }
  if (c.tier !== null) {
    // Score and tier are one judgement, read together — the score alone means nothing without
    // the tier it sits in (see the ranking explainer: the score only breaks ties inside a tier).
    const tier = capitalize(c.tier);
    signals.push({
      label: 'Dependability',
      value: c.score === null ? tier : `${c.score} · ${tier}`,
    });
  }
  // Only an affirmative favorite is evidence; "No" would be a chip that says nothing.
  if (c.favorite) signals.push({ label: 'Favorite', value: 'Yes' });
  if (c.shifts_with_client !== null) {
    // A PROVEN zero belongs here: "measured, and they have never worked here" is a real fact the
    // ranking used. Only null (never measured) is left out.
    signals.push({
      label: 'Prior shifts here',
      value: String(c.shifts_with_client),
    });
  }
  return signals;
};
const checkObservedFor = (
  c: WirePersistedCheck,
  timezone: string,
): LivePersistedCheck['observed'] => {
  if (c.observed_kind === 'timestamp')
    return {
      kind: 'timestamp',
      // Rendered here rather than in the modal: the observed instant is UTC on the wire and only
      // this layer knows the run's venue zone.
      value:
        c.observed_at === null
          ? null
          : formatVenueTime(c.observed_at, timezone, 'datetime'),
    };
  if (c.observed_kind === 'none') return { kind: 'none', value: null };
  return { kind: c.observed_kind, value: c.observed_number };
};
/*
 * F4 (GRAVY-10816 stub sweep): the modal's timeline printed graphy's raw persisted step keys
 * ("discovered", "evaluated") straight at the operator.
 *
 * The keys the backend records today are declared closed on the contract (WIRE_TIMELINE_KEYS), so
 * this map is provably exhaustive over them. The wire field itself stays `string`: the backend
 * owns the step list and may persist a new step before this contract catches up. An unexpected
 * key is HUMANISED rather than echoed raw or dropped — dropping it would silently shorten the
 * recorded history of a run, which is the one thing this modal exists to show.
 *
 * S4 (independent review): `offer_created` is one such step the contract had not caught up with —
 * job_detail_builder emits one entry per offer alongside the four run steps, so a LIVE run's
 * timeline reached the modal with its offers rendered by the fallback. It is a declared key with
 * a sentence of its own now; the entry is per-offer, so the sentence names the offer, not a stage
 * the whole run passed through.
 */
const TIMELINE_LABELS: Record<WireTimelineKey, string> = {
  discovered: 'Discovered by First Responder',
  triggered: 'Trigger time reached',
  evaluated: 'Evaluated',
  completed: 'Completed',
  offer_created: 'Offer created',
};

export const timelineLabelFor = (key: string): string =>
  TIMELINE_LABELS[key as WireTimelineKey] ?? capitalize(key.replace(/_/g, ' '));

/**
 * rank IS selection_rank. No coercion and no filtering: WireSelectedCandidate guarantees the field
 * is present, so every row the payload declares as selected is rendered. A malformed row now
 * fails the contract rather than vanishing from the list.
 */
export const toCandidate = (
  c: WireSelectedCandidate,
  ranking: LiveRankingPolicy | null = null,
): LiveCandidate => ({
  rank: c.selection_rank,
  displayName: internalWorkerLabel(c.worker_id),
  tier: c.tier,
  score: c.score,
  distanceMiles: c.distance_miles,
  decision: c.action_decision,
  decisionLabel:
    c.action_decision === null
      ? NO_ACTION_RECORDED_LABEL
      : decisionLabelFor(c.action_decision),
  decisionTone:
    c.action_decision === null ? 'plain' : decisionToneFor(c.action_decision),
  // Composed from the row's own persisted facts (candidateSignals above); never an empty drawer.
  signals: candidateSignals(c, ranking),
});

export const toCheck = (
  c: WirePersistedCheck,
  timezone: string,
): LivePersistedCheck => ({
  key: c.key,
  passed: c.passed,
  observed: checkObservedFor(c, timezone),
  recordedAt: formatVenueTime(c.recorded_at, timezone, 'datetime'),
  runId: c.run_id,
});

// Task 11: the label is a REAL sentence for every closed key, shared with the preview through
// EXCLUSION_LABELS so a fixture row and a live row in the same gate read identically. It used to
// be the raw key, which put "lta_elsewhere" in front of an operator.
export const toExclusion = (e: WireExclusionGroup): LiveExclusionGroup => ({
  key: e.key,
  // A gate key the contract does not know yet is humanised, never shown as its machine key.
  label: EXCLUSION_LABELS[e.key] ?? capitalize(e.key.replace(/_/g, ' ')),
  count: e.count,
  supported: e.supported,
});

export const toTimelineEntry = (
  t: WireTimelineEntry,
  timezone: string,
): LiveTimelineEntry => ({
  at: t.at === null ? null : formatVenueTime(t.at, timezone, 'datetime'),
  label: timelineLabelFor(t.key),
});

export const toRunMeta = (w: FirstResponderJobWire): LiveRunMeta => ({
  runId: w.job.run_meta.run_id,
  evaluatedAt: formatVenueTime(
    w.job.run_meta.evaluated_at,
    w.job.timezone,
    'datetime',
  ),
  policyVersion: w.job.run_meta.policy_version,
  rankingVersion: rankingVersionLabel(toRankingPolicy(w.job.run_meta.ranking)),
});

export const toFunnel = (w: FirstResponderJobWire): LiveFunnel => ({
  poolSize: w.job.funnel.pool_size,
  passedGates: w.job.funnel.passed_gates,
  selectedCount: w.job.funnel.selected_count,
  invitedCount: w.job.funnel.invited_count ?? null,
});

// Fix round 1 (Task 7 review, FINDING 3): the selected-count/list-count invariant is CHECKED, not
// merely carried through. `mapJobDetail`/`toJobDetail` used to map whatever `candidates` array it
// was handed without ever comparing it against the run's own persisted `funnel.selected_count` —
// a genuine backend inconsistency would render silently, with the modal's candidate list quietly
// disagreeing with the funnel diagram right above it. Ruling: fail loud, never silently drop the
// extra/missing rows, never blank the page — the recorded candidate rows still render underneath
// a visible warning.
const integrityWarningFor = (
  candidatesLength: number,
  selectedCount: number,
): string | null =>
  candidatesLength === selectedCount
    ? null
    : `Recorded selection lists ${candidatesLength} worker${candidatesLength === 1 ? '' : 's'} but the run reports ${selectedCount} selected. Showing the recorded rows.`;

/*
 * F2 (GRAVY-10816 stub sweep): a failed run's evidence label was UNKNOWN_LABEL — the one place
 * the console has to explain an infrastructure failure said nothing at all.
 *
 * The accepted view model already names the sentence (liveViewModel.ts LiveFailureEvidence) and
 * the preview already renders it, so SAFE_FAILURE_LABEL now lives beside the type and both
 * consumers import it rather than keeping two copies to drift.
 *
 * The error-code set is closed, so this is exhaustive today and a new code fails to compile until
 * it is given words. The `??` is defensive only: a server that broke the contract still gets the
 * honest investigation sentence rather than a raw code or a bare "Unknown". Vagueness about the
 * CAUSE is deliberate — the wire carries a code and a run id, never exception text, so naming a
 * cause here would be a guess presented to an operator as evidence.
 */
const FAILURE_LABELS: Record<WireErrorCode, string> = {
  evaluation_error: SAFE_FAILURE_LABEL,
};

export const failureLabelFor = (code: WireErrorCode): string =>
  FAILURE_LABELS[code] ?? SAFE_FAILURE_LABEL;

/**
 * Every job-detail field mapped, including this job's OWN attention history. GET .../jobs/:id is
 * only ever called for a job with a persisted run (a real 'watching' row 404s and is never
 * fetched — see Connected/index.tsx), so state here is never 'watching' in production; STATE_META
 * still covers it exhaustively so an unexpected value renders honestly rather than crashing.
 */
// GRAVY-10816 second sweep: a shadow run's picks carry the wire decision `will_offer`, and the badge
// read "WILL OFFER" under a run whose own state reads "WOULD OFFER". Nothing will be offered in
// Shadow, so on a shadow_selected run the label says what the run says.
export const shadowDecision = (
  c: LiveCandidate,
  shadow: boolean,
): LiveCandidate =>
  shadow && c.decision === 'will_offer'
    ? { ...c, decisionLabel: 'WOULD OFFER', decisionTone: 'blue' }
    : c;

export const toJobDetail = (w: FirstResponderJobWire): LiveJobDetail => {
  const timezone = w.job.timezone;
  // The version THIS run ranked under — the legend and every pick's drawer describe it.
  const ranking = toRankingPolicy(w.job.run_meta.ranking);
  // Task 11: one venue-clock pair for the modal's header line. Same-day elision drops the
  // repeated date from the second half, so it reads "Shift starts Sep 6, 6:31 PM CDT · First
  // Responder acts 9:46 AM CDT".
  const [shiftStartsAt, actsAt] = formatVenueRange(
    w.job.shift_starts_at,
    w.job.acts_at,
    timezone,
  );
  const triggeredAt = formatVenueTime(w.job.acts_at, timezone, 'datetime');
  return {
    id: w.job.id,
    jobId: w.job.job_id,
    orderId: w.job.order_id,
    title: w.job.title,
    client: w.job.client,
    shiftStartsAt,
    actsAt,
    state: w.job.state,
    stateLabel: stateLabelFor(w.job.state),
    tone: stateToneFor(w.job.state),
    // M6 (modal follow-up): acts_at is the TRIGGER time, never a past "evaluated" instant — the
    // same correction as nextForJobRow's. The real evaluation instant (run_meta.evaluated_at) is
    // already surfaced separately below (toRunMeta), so this is a label fix, not a new value.
    // The header's acts value may have had its (repeated) date elided; this line stands alone
    // above the funnel, so it always carries the full instant.
    next: {
      kind: 'text',
      text:
        triggeredAt === VENUE_TIME_UNKNOWN
          ? UNKNOWN_LABEL
          : `Triggered ${triggeredAt}`,
    },
    runMeta: toRunMeta(w),
    checks: w.job.checks.map((c) => toCheck(c, timezone)),
    funnel: toFunnel(w),
    funnelStage: w.job.funnel_stage,
    // Every declared row is mapped - no filter. Ordered by selection_rank, with worker_id as a
    // deterministic tie-break so the list can never reorder between renders.
    candidates: [...w.job.candidates]
      .sort(
        (a, b) =>
          a.selection_rank - b.selection_rank || a.worker_id - b.worker_id,
      )
      .map((c) =>
        shadowDecision(
          toCandidate(c, ranking),
          w.job.state === 'shadow_selected',
        ),
      ),
    rankingExplainer: rankingExplainerFor(ranking),
    selectedWorkerIds: w.job.selected_worker_ids.map(String),
    workersAvailableTotal: w.job.workers_available_total,
    exclusions: w.job.exclusions.map(toExclusion),
    timeline: w.job.timeline.map((t) => toTimelineEntry(t, timezone)),
    attentionEvents: w.job.attention_events.map(toAttentionEvent),
    failure:
      w.job.failure === null
        ? null
        : {
            errorCode: w.job.failure.error_code,
            label: failureLabelFor(w.job.failure.error_code),
            runId: w.job.failure.run_id,
          },
    integrityWarning: integrityWarningFor(
      w.job.candidates.length,
      w.job.funnel.selected_count,
    ),
  };
};
